import { FC, MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useWxWork } from '@cloud/wxwork-workbench';
import PageLayout from '@/layouts/page';
import List, { ActionType as ListActionType } from '@/components/List';
import Button from '@/components/Button';
import Tag, { TagProps } from '@/components/Tag';
import ResponsiveSelect from '@/components/ResponsiveSelect';
import Toast from '@/components/Toast';
import Tooltip from '@/components/Tooltip';
import Badge, { BadgeStatus } from '@/components/Badge';
import DropdownMenu from '@/components/DropdownMenu';
import HelpTips from '@/components/HelpTips';
import ScreenContext from '@/contexts/screen';
import { go, goReplace, maskCode } from '@/utils';
import { smallSizeWidth } from '@/constants';
import { GlobalConfigResult, TaskType } from '../service';
import { CustomerListItem, addCustomer, queryCustomerList, queryTask } from './service';
import RecordDrawer from './record';
import styles from './index.less';


export enum CustomerStatus {
  toadd = 'WAIT_ADD',
  expecting = 'WAIT_PASS',
  finished = 'FINISH_ADD'
}

export type Analysis = Record<CustomerStatus, number>

export interface TaskDetailProps {
  config: GlobalConfigResult;
  onBack?: () => void;
}

const notification = '如果手机号码所对应微信用户已经是你的好友，请在企业微信联系人详情页为其备注该手机号，系统会自动识别为已添加，此过程可能存在延迟，备注后请耐心等待。';

const filterSelectOptions = [
  { value: '_all_', label: '全部', tagType: 'default' },
  { value: 'A', label: '有明确意向', tagType: 'success' },
  { value: 'B', label: '可能有意向', tagType: 'warning' },
  { value: 'C', label: '用户拒绝', tagType: 'error' },
  { value: 'D', label: '用户忙', tagType: 'processing' },
  { value: 'E', label: '拨打失败', tagType: 'disabled' }
]

const tabs = [
  { key: CustomerStatus.toadd, title: '待添加' },
  { key: CustomerStatus.expecting, title: '待通过' },
  { key: CustomerStatus.finished, title: '已添加' }
]

const defaultAnalysis = {
  [CustomerStatus.toadd]: 0,
  [CustomerStatus.expecting]: 0,
  [CustomerStatus.finished]: 0
}

const TaskDetailPage: FC<TaskDetailProps> = ({ config, onBack }) => {
  const history = useHistory();
  const { id, status } = useParams<{ id?: string, status?: string }>();
  const listActionRef = useRef<ListActionType>();
  const signature = useWxWork();
  const { width: screenWidth } = useContext(ScreenContext);
  const [filterStatus, setFilterStatus] = useState<string>(filterSelectOptions[0].value);
  const [detail, setDetail] = useState<CustomerListItem['phone'] | void>();
  const [taskName, setTaskName] = useState<string>('');
  const [taskType, setTaskType] = useState<TaskType>('ASSIGN');
  const [analysis, setAnalysis] = useState<Analysis>(defaultAnalysis);
  const [copySubmiting, setCopySubmiting] = useState<boolean>(false);

  useEffect(() => {
    if (!id) {
      onBack();
    }
    let rightStatus = false;
    for (let k in CustomerStatus) {
      rightStatus = (CustomerStatus[k] === status);
      if (rightStatus) break;
    }
    if (!rightStatus) {
      const parent = history.location.pathname.split('/').filter(x => x !== status).join('/');
      goReplace(history, `${parent}/${CustomerStatus.toadd}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTaskInfo = useCallback(async () => {
    const res = await queryTask(Number(id), signature);
    setTaskName(res.taskName);
    setTaskType(res.addFriendType);
    setAnalysis(res.status);
  }, [id, signature]);

  useEffect(() => {
    if (signature.corpId && signature.userId) {
      getTaskInfo().then(() => {
        if (listActionRef.current) {
          listActionRef.current.refresh();
        }
      })
    }
  }, [status, filterStatus, signature, getTaskInfo]);

  const handleTabChange = useCallback((k: CustomerStatus) => {
    listActionRef.current.clear();
    const parent = history.location.pathname.split('/').filter(x => x !== status).join('/');
    goReplace(history, `${parent}/${k}`);
  }, [status, history]);

  const handleCopyNumber = useCallback(async (value: CustomerListItem['phone']) => {
    if (!copySubmiting) {
      try {
        setCopySubmiting(true);
        await addCustomer(value, signature);
        await getTaskInfo();
        setCopySubmiting(false);
        Toast.info('已复制手机号，请前往企业微信添加', 1000);
        listActionRef.current.refresh();
      } finally {}
    }
  }, [copySubmiting, signature, getTaskInfo]);

  const handleCheckRecords = (e: MouseEvent | null, number: CustomerListItem['phone']) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setDetail(number);
  }

  const handleReportUnavailability = useCallback((e: MouseEvent | null, number: CustomerListItem['phone']) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const parent = history.location.pathname.split('/').filter((_, i) => i < 2).join('/');
    go(history, `${parent}/report-unavailability/${number}`);
  }, [history]);

  const fetchList = useCallback(async pagination => {
    try {
      const res = await queryCustomerList({
        status,
        ...pagination,
        total: analysis[status],
        taskId: Number(id),
        callIntention: filterStatus === filterSelectOptions[0].value ? undefined : filterStatus,
        auth: signature
      });
      return { data: res.data, pagination: res.pagination };
    } catch (e) {
      goReplace(history, '/add-friend-tasks/deleted');
    }
  }, [history, id, status, analysis, filterStatus, signature]);

  const ai = useMemo(() => /AI/.test(taskType), [taskType]);

  const renderTitle = useCallback(() =>
    <div className={styles.title}>
      <h2>{taskName}</h2>
      <div className={styles.filter}>
        {ai ? (
          <ResponsiveSelect
            width={100}
            value={filterStatus}
            options={filterSelectOptions}
            onChange={setFilterStatus}
            renderSmallItem={({ label, value: k }) => {
              if (k === filterSelectOptions[0].value) {
                return label;
              }
              return (
                <Badge status={filterSelectOptions.find(({ value: v }) => v === k).tagType as BadgeStatus}>
                  {label}
                </Badge>
              )
            }}
          />
        ) : null}
      </div>
    </div>, [ai, filterStatus, taskName])

  const renderItem = useCallback((phone: string, at: number, ci: string, cm: string, aft: string, nn: string, needCopy: boolean, canCopy: boolean) =>
    <div className={styles.item}>
      <div className={styles.info}>
        <h4 className={styles.number}>
          {canCopy ? phone : maskCode(phone)}
        </h4>
        {(ai && status !== CustomerStatus.finished) ? (
          <div className={styles.tag}>
            <Tag type={filterSelectOptions.find(({ value: v }) => ci === v)?.tagType as TagProps['type']}>
              {filterSelectOptions.find(({ value: v }) => ci === v)?.label}
            </Tag>
          </div>
        ) : null}
        <div className={styles.actions}>
          {needCopy ? (canCopy ? <Button type='link'>复制</Button> : (
            <Tooltip title='今日添加好友已达上限，无法复制手机号，请联系管理员' placement='top'>
              <Button type='link' disabled>复制</Button>
            </Tooltip>
          )) : null}

          {status === CustomerStatus.finished ? (
            <Button type='link' onClick={e => handleCheckRecords(e, phone)}>分配记录</Button>
          ) : (screenWidth < smallSizeWidth ? (
            <DropdownMenu
              options={[
                { key: 'allocatedCount', title: '分配记录' },
                ...(status === CustomerStatus.expecting ? [{ key: 'unabailable', title: '标记无效' }] : [])
              ]}
              onSelect={key => {
                switch (key) {
                  case 'allocatedCount':
                    handleCheckRecords(null, phone);
                    return;
                  case 'unabailable':
                    handleReportUnavailability(null, phone);
                    return;
                  default:
                    return;
                }
              }}
            >
              <Button type='link'>···</Button>
            </DropdownMenu>
          ) : (
            <>
              <Button type='link' onClick={e => handleCheckRecords(e, phone)}>分配记录</Button>
              {status === CustomerStatus.expecting ? (
                <Button type='link' onClick={e => handleReportUnavailability(e, phone)}>标记无效</Button>
              ) : null}
            </>
          ))}

        </div>
      </div>
      <div className={styles.additionalInfo}>
        {(nn && status === CustomerStatus.finished) ? (
          <div className={styles.nickname}>
            昵称：
            <Tooltip title={nn} placement='topLeft'>
              <span>{nn}</span>
            </Tooltip>
          </div>
        ) : null}
        {(cm && status === CustomerStatus.expecting) ? (
          <div
            className={styles.copiedTime}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              Toast.info((
                <div>
                  <p>最近一次复制手机号执行添加的时间</p>
                  <p>{cm}</p>
                </div>
              ), 3000);
            }}
          >复制时间：{cm}</div>
        ) : null}
        {(aft && status === CustomerStatus.finished) ? (
          <div
            className={styles.addFriendTime}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              Toast.info((
                <div>
                  <p>客户添加员工为好友的时间</p>
                  <p>{aft}</p>
                </div>
              ), 3000);
            }}
          >
            添加时间：{aft}
          </div>
        ) : null}
      </div>
    </div>, [ai, handleReportUnavailability, screenWidth, status]);
  
  return (
    <>
      {screenWidth < smallSizeWidth ? (
        <div className={styles.notice}>{notification}</div>
      ) : null}
      <PageLayout
        title={renderTitle()}
        notification={screenWidth < smallSizeWidth ? undefined : notification}
        onBack={screenWidth < smallSizeWidth ? undefined : onBack}
        extra={<HelpTips />}
        tabs={tabs.map(({ key, title }) => ({
          key, title,
          count: analysis[key]
        }))}
        activeKey={status}
        onTabChange={handleTabChange}
      >
        <div className={styles.main}>
          <List
            actionRef={r => listActionRef.current = r}
            request={fetchList}
            params={{ aiFilter: filterStatus }}
            loadDataLength={50}
            scrollThreshold='0px'
            onClickItem={status !== CustomerStatus.finished ? () => { } : undefined}
            renderItem={useCallback(({ phone, allocationTimes: at, callIntention: ci, dailyAddFriendTimes: daft, copyTime: cm, addFriendTime: aft, name: nn }) => {
              const canCopy = !config?.filter || daft < config?.times;
              const needCopy = status !== CustomerStatus.finished;
              if (needCopy && canCopy) {
                return (
                  <CopyToClipboard text={phone} onCopy={handleCopyNumber}>
                    {renderItem(phone, at, ci, cm, aft, nn, needCopy, canCopy)}
                  </CopyToClipboard>
                );
              }
              return renderItem(phone, at, ci, cm, aft, nn, needCopy, canCopy);
            }, [status, config, handleCopyNumber, renderItem])}
          />
        </div>
        <RecordDrawer id={detail} onClose={() => setDetail()} />
      </PageLayout>
    </>
  );
}

export default TaskDetailPage;